import { FlexProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import { useState } from 'react';
import { DepositoryAccountSelector } from '../financialEntities/DepositoryAccountSelectionView';
import { useCheckDepositoryAccount } from '../financialEntities/helpers';
import { useGoal } from './helpers';
import OnetimeTransactionCardLayout from './TransactionCardLayout';

export interface TransactionDepositoryAccountCardProps extends FlexProps {
    title: string;
    subtitle: string;
    goalId: string;
    depositoryAccountId: string | undefined;
    onChangeDepositoryAccountId: (
        depositoryAccountId: string | undefined
    ) => void;
    onBack?: VoidFunction;
    onNext: VoidFunction;
    onClose: VoidFunction;
}

const TransactionDepositoryAccountCard = ({
    title,
    subtitle,
    goalId,
    depositoryAccountId,
    onChangeDepositoryAccountId,
    onBack,
    onNext,
    onClose,
    ...props
}: TransactionDepositoryAccountCardProps) => {
    const { checkDepositoryAccount, errorMessageByAccountId } =
        useCheckDepositoryAccount({
            setSelectedAccountId: onChangeDepositoryAccountId,
        });
    const { goal } = useGoal(goalId);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const checkAccountAndContinue = async () => {
        assert(depositoryAccountId);
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            assert(goal?.accountStructure, 'Missing goal account structure');
            await checkDepositoryAccount(depositoryAccountId);
            onChangeDepositoryAccountId(depositoryAccountId);
            onNext();
        } catch {
            // do nothing; allow checkDepositoryAccount to handle errors
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OnetimeTransactionCardLayout
            {...props}
            title={title}
            subtitle={subtitle}
            onBack={onBack}
            onNext={checkAccountAndContinue}
            isNextDisabled={
                isSubmitting || !depositoryAccountId || !goal?.accountStructure
            }
            isNextLoading={isSubmitting}
            onClose={onClose}
        >
            <DepositoryAccountSelector
                selectedAccountId={depositoryAccountId}
                onChangeSelectedAccountId={onChangeDepositoryAccountId}
                errorMessageByAccountId={errorMessageByAccountId}
                isDisabled={isSubmitting}
            />
        </OnetimeTransactionCardLayout>
    );
};

export default TransactionDepositoryAccountCard;
